'use client';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useRef, useState } from 'react';

import { ShopButton } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/shop-button';
import { ProductDetailsModal } from '@/modules/players/v3/components/shared/gear-card/product-details-modal';
import { Gear } from '@/modules/players/v3/types';

interface PickleballCentralGearCardProps {
  gear: Gear;
  isWebview?: boolean;
}

const formatPrice = (value: number) => `$${value.toFixed(2)}`;

export const PickleballCentralGearCard = ({ gear, isWebview }: PickleballCentralGearCardProps) => {
  const images = [...gear.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const dragStartX = useRef<number | null>(null);

  const goTo = (index: number) => setCurrentIndex(Math.max(0, Math.min(images.length - 1, index)));
  const goToPrev = () => goTo(currentIndex - 1);
  const goToNext = () => goTo(currentIndex + 1);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;
  const hasMultiple = images.length > 1;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartX.current === null) return;
    const delta = e.clientX - dragStartX.current;
    if (hasMultiple && Math.abs(delta) > 30) {
      if (delta < 0) goToNext();
      else goToPrev();
    } else {
      setShowDetails(true);
    }
    dragStartX.current = null;
  };

  const showStrikethrough = gear.onSale || Boolean(gear.discountPercentage);

  const priceBlock = (
    <div className='flex flex-1 flex-col items-start justify-center'>
      {showStrikethrough ? (
        <span className='relative inline-block text-sm leading-5 text-gray-700 md:text-xs md:leading-[18px]'>
          {formatPrice(gear.price)}
          <span
            aria-hidden='true'
            className='pointer-events-none absolute -inset-x-0.5 top-1/2 h-[1.5px] -translate-y-1/2 -rotate-[7.61deg] bg-gray-700'
          />
        </span>
      ) : null}
      <span
        className={clsx(
          'text-base font-bold leading-6 md:text-sm md:leading-5',
          showStrikethrough ? 'text-error-500' : 'text-gray-900'
        )}
      >
        {formatPrice(gear.finalPrice)}
      </span>
    </div>
  );

  return (
    <div className='flex h-full w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white'>
      <div className='relative aspect-square w-full border-b border-gray-200 bg-white'>
        {gear.discountPercentage ? (
          <div className='absolute left-2 top-2 z-10 rounded-full bg-error-500 px-2 py-0.5 text-xs font-semibold leading-[18px] text-white'>
            {gear.discountPercentage}% off
          </div>
        ) : null}
        <div
          className={clsx(
            'relative size-full select-none',
            hasMultiple ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
          )}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          {images.map((image, i) => (
            <img
              key={image.id}
              src={image.urlThumbnail}
              alt={i === 0 ? gear.name : ''}
              aria-hidden={i !== currentIndex}
              className={clsx(
                'pointer-events-none absolute inset-0 size-full object-contain',
                i !== currentIndex && 'hidden'
              )}
              draggable={false}
            />
          ))}
        </div>

        {hasMultiple ? (
          <>
            <div className='absolute right-2 top-2 flex items-center justify-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium leading-[18px] text-gray-700'>
              {`${currentIndex + 1}/${images.length}`}
            </div>
            {hasPrev ? (
              <button
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToPrev();
                }}
                aria-label='Previous image'
                className='absolute left-0 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-lg p-2 opacity-30 transition-opacity hover:opacity-60'
              >
                <FontAwesomeIcon icon={faChevronLeft} className='size-4 text-gray-900' />
              </button>
            ) : null}
            {hasNext ? (
              <button
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label='Next image'
                className='absolute right-0 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-lg p-2 opacity-30 transition-opacity hover:opacity-60'
              >
                <FontAwesomeIcon icon={faChevronRight} className='size-4 text-gray-900' />
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      <div className='flex flex-1 flex-col justify-between gap-4 px-3 pb-2 pt-4'>
        <button
          type='button'
          onClick={() => setShowDetails(true)}
          className='w-full text-left'
          aria-label={`View ${gear.name} details`}
        >
          <p className='line-clamp-2 min-h-10 w-full text-sm font-medium leading-5 text-gray-900'>
            {gear.name}
          </p>
        </button>
        {isWebview ? (
          <div className='flex items-end gap-2'>{priceBlock}</div>
        ) : (
          <a
            href={gear.url}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`Shop ${gear.name} on Pickleball Central (opens in a new tab)`}
            className='flex flex-wrap items-end gap-2 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-darkorange-400 focus-visible:ring-offset-2'
          >
            {priceBlock}
            <ShopButton className='flex-1 whitespace-nowrap'>
              <span>Shop now</span>
            </ShopButton>
          </a>
        )}
      </div>

      <ProductDetailsModal
        gear={gear}
        open={showDetails}
        onOpenChange={setShowDetails}
        isWebview={isWebview}
      />
    </div>
  );
};
